import ProductTypeLabel from 'LuxKit/Label/ProductTypeLabel'
import useCruiseOfferView from 'hooks/Cruise/useCruiseOfferView'
import { isCruiseOffer } from 'lib/offer/offerTypes'
import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton'
import type { SearchTileLogo } from '../SearchTile'
import SearchTile from '../SearchTile'
import SearchTileRowOverlineJourney from '../shared/SearchTileRowOverlineJourney'
import CruiseSearchTileFeatures from './CruiseSearchTileFeatures'
import CruiseSearchTileLabels from './CruiseSearchTileLabels'
import CruiseSearchTileMeta from './CruiseSearchTileMeta'
import CruiseSearchTilePriceStack from './CruiseSearchTilePriceStack'
import TextButton from 'LuxKit/Button/TextButton'
import useCruiseSelectedCabinType from 'hooks/useCruiseSelectedCabinType'
import { useCurrentSiteTakeover } from 'takeovers/utils/hooks/useCurrentSiteTakeover'
import { trimCruiseShipNameFromTitle } from 'lib/cruises/cruiseUtils'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import LineShipIcon from 'LuxKit/Icons/line/LineShipIcon'
import Caption from 'LuxKit/Typography/Caption'
import { StyledSvgIcon, setSvgIconSize } from 'LuxKit/SvgIcon'
import { rem } from 'polished'
import styled from 'styled-components'
import { hasAnyBundleFlightCredits } from 'lib/cruises/cruiseBundleFlightCredits'

const CruiseBundledHeroPromoCaption = styled(Caption).attrs({
  variant: 'smallalt',
  weight: 'normal',
})`
  && {
    color: ${(p) => p.theme.palette.highlight.primary.lightContrast};
  }
`

const CRUISE_BUNDLE_HOTEL_ITEM_TYPES = new Set(['PRE_ACCOMMODATION', 'POST_ACCOMMODATION'])

function bundleHasCruiseHotelItems(bundle: App.CruiseOfferBundle): boolean {
  return bundle.items.some((item) => CRUISE_BUNDLE_HOTEL_ITEM_TYPES.has(item.itemType))
}

const CruiseBundledHeroPromo = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${rem(4)};
  max-width: fit-content;
  min-height: ${rem(18)};
  padding: ${rem(2)} ${rem(8)};
  white-space: nowrap;
  border-radius: ${(p) => p.theme.borderRadius.S};
  border: 1px solid ${(p) => p.theme.palette.highlight.primary.normalForeground};
  background-color: ${(p) => p.theme.palette.highlight.primary.normalForeground};
  color: ${(p) => p.theme.palette.highlight.primary.lightContrast};

  > ${StyledSvgIcon} {
    ${setSvgIconSize(12)}
  }
`

interface Props {
  offer: App.CruiseOffer | App.TourOffer | App.TourOfferSummary
  filters?: App.OfferListFilters
}

function CruiseOfferTile({ offer, filters }: Props) {
  const intl = useIntl()
  const selectedCabinType = useCruiseSelectedCabinType(isCruiseOffer(offer) ? offer.cruiseType : undefined)
  const view = useCruiseOfferView({ offer, cabinTypes: selectedCabinType || undefined, filters })

  const logo = useMemo((): SearchTileLogo => ({
    compact: { id: view.cruiseLineImage, title: view.cruiseLineName },
    full: { id: view.cruiseLineImage, title: view.cruiseLineName },
  }), [view])

  const title = useMemo(() => trimCruiseShipNameFromTitle(offer.name), [offer.name])

  const offerSiteTakeover = useCurrentSiteTakeover({ offer })
  const viewOfferLabel = intl.formatMessage({
    id: '1TDNDgzK3pnQZCGqQ7VW',
    defaultMessage: 'View offer',
    description: 'Button text to view cruise offer details in the cruise section',
  })
  const { productLabel, tileChrome } = useMemo(() => {
    const typeLabel = <ProductTypeLabel productType={offer.productType} />
    const defaultChrome = { productLabel: typeLabel, tileChrome: 'default' as const }
    if (!isCruiseOffer(offer) || !offer.bundle?.rateCodes.length) {
      return defaultChrome
    }
    const hasBundleHotels = bundleHasCruiseHotelItems(offer.bundle)
    const hasBundleFlightCredits = hasAnyBundleFlightCredits(offer.bundle)

    if (!hasBundleHotels && !hasBundleFlightCredits) {
      return defaultChrome
    }

    let bundleTitle = intl.formatMessage({
      id: 'YTJIZPQgudsTc34xUMg_',
      defaultMessage: 'Cruise + Hotel',
      description: 'Tag label for cruise booking that includes hotel accommodation',
    })

    if (hasBundleHotels && hasBundleFlightCredits) {
      bundleTitle = intl.formatMessage({
        id: 'gkCGFU8iEqY1ZySPPxCZ',
        defaultMessage: 'Cruise + Hotel + Flight credits',
        description:
          'Search tile promo label when a cruise bundle includes both hotel accommodation and flight credits.',
      })
    }

    if (!hasBundleHotels && hasBundleFlightCredits) {
      bundleTitle = intl.formatMessage({
        id: 'Vsqk2aCOOtcfvLCrfwef',
        defaultMessage: 'Cruise + Flight credits',
        description: 'Search tile promo label when a cruise bundle includes flight credits but no hotel items.',
      })
    }

    return {
      productLabel: <LabelGroup>
        <CruiseBundledHeroPromo>
          <LineShipIcon aria-hidden />
          <CruiseBundledHeroPromoCaption variant="medium">{bundleTitle}</CruiseBundledHeroPromoCaption>
        </CruiseBundledHeroPromo>
        {typeLabel}
      </LabelGroup>,
      tileChrome: 'cruise-bundle' as const,
    }
  }, [offer, intl])

  return <SearchTile
    banner={<>{offerSiteTakeover?.searchPage?.tileBanner}</>}
    action={<TextButton nonInteractable kind="primary">{viewOfferLabel}</TextButton>}
    bookmarkAction={<BookmarkButton offer={offer} />}
    features={<CruiseSearchTileFeatures view={view} filters={filters} />}
    images={offer.images}
    labels={<CruiseSearchTileLabels view={view} />}
    logo={logo}
    meta={<CruiseSearchTileMeta view={view} />}
    overline={<SearchTileRowOverlineJourney start={view.departureCity} end={view.destinationCity} />}
    pricePoint={<CruiseSearchTilePriceStack view={view} />}
    productLabel={productLabel}
    tileChrome={tileChrome}
    title={title}
    offer={offer}
  />
}

export default CruiseOfferTile
